/**
 * Slash Commands page. Figma `9229:190530` (content `9229:190686`).
 *
 * Section titles `9229:190695` / `9229:190739`: 14/22 medium, Color/text/primary,
 * px 8, pb 14, no top padding (bare). The 24px gap between the two blocks is
 * `SettingsPane`. Rows `9229:190702`: 54px, px 20, py 16. Both the spelling and
 * the name are Color/text/primary (`--app-color-text-primary`), 14/22. The
 * card is the shared 14px / gray100-80% card.
 *
 * The frame's hairline is inset 20px. This page uses the window's full-bleed
 * `SettingsHairline` so the card matches every other page in the same window.
 */
import type { ChannelClient } from '@hibro/platform-core';
import type {
  ILauncherService,
  IModuleService,
  IWhenContextService,
  LaunchItem,
  ResolvedActivityBarItem,
  ResolvedCommand,
  WhenContext,
  WhenContextChange,
} from '@hibro/shared';
import {
  CONTRIBUTIONS_CHANGED_EVENT,
  LAUNCHER_CHANGED_EVENT,
  WHEN_CONTEXT_CHANGED_EVENT,
} from '@hibro/shared';
import {
  SETTINGS_CARD,
  SETTINGS_ROW,
  SettingsHairline,
  SettingsHint,
  SettingsPane,
} from '@hibro/ui-client';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { BUILTIN_COMMANDS } from '../../commands/builtin-commands';
import { useTranslator } from '../../i18n/i18n';
import { settingsWindowLogError, settingsWindowLogWarn } from './log';
import { collectSlashCommands, type SlashCommandRow } from './slash-commands';

function failureReason(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function SlashCommandsPage({
  moduleService,
  launcherService,
  client,
  whenContextService,
}: {
  moduleService: IModuleService;
  launcherService?: ILauncherService;
  client?: ChannelClient;
  whenContextService?: IWhenContextService;
}): ReactNode {
  const { t, title } = useTranslator();
  const [contributed, setContributed] = useState<
    readonly ResolvedCommand[] | null
  >(null);
  const [activityBar, setActivityBar] = useState<
    readonly ResolvedActivityBarItem[]
  >([]);
  const [commandError, setCommandError] = useState<string | null>(null);
  const [launchItems, setLaunchItems] = useState<readonly LaunchItem[] | null>(
    null,
  );
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [moduleContext, setModuleContext] = useState<WhenContext>({});

  useEffect(() => {
    let active = true;
    const load = () => {
      void moduleService
        .listContributions()
        .then((next) => {
          if (!active) {
            return;
          }
          if (
            !Array.isArray(next.commands) ||
            !Array.isArray(next.activityBar)
          ) {
            const reason = 'contributions omitted commands or activityBar';
            settingsWindowLogError('settings slash commands load failed', {
              reason,
            });
            setCommandError(reason);
            return;
          }
          setContributed(next.commands);
          setActivityBar(next.activityBar);
          setCommandError(null);
        })
        .catch((err: unknown) => {
          if (!active) {
            return;
          }
          const reason = failureReason(err);
          settingsWindowLogError('settings slash commands load failed', {
            reason,
          });
          setCommandError(reason);
        });
    };
    load();
    if (!client) {
      return () => {
        active = false;
      };
    }
    const dispose = client.listen(CONTRIBUTIONS_CHANGED_EVENT, load);
    return () => {
      active = false;
      dispose();
    };
  }, [moduleService, client]);

  useEffect(() => {
    if (!launcherService) {
      settingsWindowLogWarn('settings slash apps unavailable', {
        reason: 'launcher service is not available',
      });
      setLaunchError('launcher service is not available');
      return;
    }
    let active = true;
    const load = () => {
      void launcherService
        .listItems()
        .then((next) => {
          if (!active) {
            return;
          }
          setLaunchItems(next);
          setLaunchError(null);
        })
        .catch((err: unknown) => {
          if (!active) {
            return;
          }
          const reason = failureReason(err);
          settingsWindowLogError('settings slash apps load failed', {
            reason,
          });
          setLaunchError(reason);
        });
    };
    load();
    if (!client) {
      return () => {
        active = false;
      };
    }
    const dispose = client.listen(LAUNCHER_CHANGED_EVENT, load);
    return () => {
      active = false;
      dispose();
    };
  }, [launcherService, client]);

  useEffect(() => {
    if (!whenContextService) {
      settingsWindowLogWarn('settings slash when-context unavailable', {
        reason: 'when-context service is not available',
      });
      return;
    }
    let active = true;
    const apply = (next: WhenContext) => {
      if (active) {
        setModuleContext(next);
      }
    };
    void whenContextService
      .getSnapshot()
      .then(apply)
      .catch((err: unknown) => {
        settingsWindowLogWarn('settings slash when-context load failed', {
          reason: failureReason(err),
        });
      });
    if (!client) {
      return () => {
        active = false;
      };
    }
    const dispose = client.listen(WHEN_CONTEXT_CHANGED_EVENT, (data) => {
      apply((data as WhenContextChange).context);
    });
    return () => {
      active = false;
      dispose();
    };
  }, [whenContextService, client]);

  const lists = useMemo(() => {
    if (!contributed) {
      return null;
    }
    return collectSlashCommands({
      commands: [...BUILTIN_COMMANDS, ...contributed],
      // Module keys are the cross-window half. This window has no editor, so
      // the workbench view keys stay unset and a clause that names them stays
      // false — the same fail-closed rule as a key nobody published.
      context: { ...moduleContext, 'workbench.ready': true },
      resolveTitle: title,
      items: launchItems ?? [],
      activityBar,
      translator: { t, title },
    });
  }, [contributed, launchItems, moduleContext, activityBar, title, t]);

  const commandRows = commandError ? null : lists?.commands;
  const appRows =
    launchError !== null || launchItems === null ? null : lists?.apps;
  const empty =
    contributed !== null &&
    commandError === null &&
    launchItems !== null &&
    launchError === null &&
    (commandRows?.length ?? 0) === 0 &&
    (appRows?.length ?? 0) === 0;

  return (
    <SettingsPane>
      {commandError ? (
        <SettingsHint>
          {t('settingsWindow.slash.loadFailed', { message: commandError })}
        </SettingsHint>
      ) : (
        <CommandCard
          title={t('settingsWindow.slash.title')}
          rows={commandRows ?? []}
        />
      )}
      {launchError ? (
        <SettingsHint>
          {t('settingsWindow.slash.appsFailed', { message: launchError })}
        </SettingsHint>
      ) : (
        <CommandCard
          title={t('settingsWindow.slash.apps')}
          rows={appRows ?? []}
        />
      )}
      {empty ? (
        <SettingsHint>{t('settingsWindow.slash.empty')}</SettingsHint>
      ) : null}
    </SettingsPane>
  );
}

function CommandCard({
  title,
  rows,
}: {
  title: string;
  rows: readonly SlashCommandRow[];
}): ReactNode {
  if (rows.length === 0) {
    return null;
  }
  return (
    <div className="flex w-full flex-col">
      <p className="w-full px-2 pb-3.5 text-[14px] leading-[22px] font-medium text-(--app-color-text-primary)">
        {title}
      </p>
      <ul className={`${SETTINGS_CARD} m-0 list-none p-0`}>
        {rows.map((row, index) => (
          <li key={row.key} className="flex w-full flex-col">
            <div className={`${SETTINGS_ROW} gap-4 py-4`}>
              <span className="min-w-0 flex-1 truncate text-[14px] leading-[22px] text-(--app-color-text-primary)">
                {row.spelling}
              </span>
              {row.detail ? (
                <span className="max-w-[45%] shrink-0 truncate text-right text-[14px] leading-[22px] text-(--app-color-text-primary)">
                  {row.detail}
                </span>
              ) : null}
            </div>
            {index === rows.length - 1 ? null : <SettingsHairline />}
          </li>
        ))}
      </ul>
    </div>
  );
}
